import React, { useContext, useEffect, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import classNames from 'classnames';

import InfiniteScroll from 'react-infinite-scroller';

import { Loader, Popup, Table } from 'semantic-ui-react';

import '../css/GradebookPlacementTable.less';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import { dateFormat } from '../utils';

import GradebookMainService from '../services/gradebook/GradebookMainService';
import GradebookPlacementService from '../services/gradebook/GradebookPlacementService';
import GradebookSummaryService from '../services/gradebook/GradebookSummaryService';

const PlacementCellContent = ({ label }) => {
  const spanRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = spanRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.offsetWidth);
    }
  });

  const span = (
    <span className='placement-cell-text' ref={spanRef}>{label}</span>
  );

  if (!isOverflowing) return span;

  return (
    <Popup
      className='placement-cell-popup'
      content={label}
      mouseEnterDelay={300}
      on='hover'
      position='top left'
      trigger={span} />
  );
};

const GradebookPlacementTable = observer((props) => {
  const { t } = props;

  const { assignmentManager, gradebookManager } = useContext(MobXProviderContext);

  const {
    activeGradebookType,
    allowLoadMoreAggregateTableRows,
    assignmentInstances,
    gradebookManagerLoadingFlagActive,
    hasMoreAggregateTableRows,
    placementTags,
  } = gradebookManager;

  const selectedTagName = placementTags?.find((tag) => tag.selected)?.name ?? null;

  const SCCheckbox = SatCoreComponent('SCCheckbox');

  const isLoading = gradebookManagerLoadingFlagActive;
  const isReadOnly = GradebookMainService.isGradebookReadOnly();

  const column = gradebookManager.activeGradebookTableSortColumn;
  const direction = gradebookManager.activeGradebookTableSortDirection;

  const [scrollerKey, setScrollerKey] = useState(0);

  const handleLoadMore = async (page) => {
    const assignment = gradebookManager.currentAssignment;
    const sortField = gradebookManager.activeGradebookTableSortColumn;
    const sortDirection = gradebookManager.activeGradebookTableSortDirection;
    await gradebookManager.fetchAggregateGradebookData(
      assignment, sortField, sortDirection, page
    );
  };

  const handleSort = async (clickedColumn) => {
    if (!allowLoadMoreAggregateTableRows) return;
    gradebookManager.setAllowLoadMoreAggregateTableRows(false);

    // reset the infinite scroller to fix page-misreporting after a sort
    setScrollerKey((prev) => prev + 1);

    await GradebookSummaryService.handleGradebookSummarySortOnBackend(clickedColumn);
    gradebookManager.setAllowLoadMoreAggregateTableRows(true);
  };

  const handleCheck = (instanceId) => {
    gradebookManager.setAssignmentInstanceBulkChecked(instanceId);
  };

  const handleCheckAll = () => {
    gradebookManager.setBulkCheckAll();
  };

  const { bulkCheckedAll } = gradebookManager;

  const renderGradeCell = (instance) => {
    return instance.grade ?? null;
  };

  const renderPlacementCell = (instance) => {
    const placement = GradebookPlacementService.getPlacement(instance.grade, selectedTagName);
    if (!placement) return null;
    return <PlacementCellContent label={placement.label} />;
  };

  return (
    <div className={classNames('gradebook-placement-table-container', activeGradebookType)}>
      <div key={scrollerKey}>
        <InfiniteScroll
          hasMore={!isLoading && allowLoadMoreAggregateTableRows && hasMoreAggregateTableRows}
          initialLoad={true}
          loadMore={(page) => allowLoadMoreAggregateTableRows && handleLoadMore(page)}>
          <Table
            className={classNames('gradebook-placement-table', { 'loading-table': isLoading })}
            sortable
            striped>
            <Table.Header>
              <Table.Row>
                {!isReadOnly && (
                  <Table.HeaderCell className={`cell-bulk-select ${activeGradebookType}`}>
                    <SCCheckbox
                      checked={bulkCheckedAll}
                      onChange={handleCheckAll}
                      tabIndex={0} />
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell
                  className={`cell-start-date ${activeGradebookType}`}
                  onClick={() => handleSort('activityTimezoneStartDate')}
                  sorted={column === 'activityTimezoneStartDate' ? direction : null}>
                  {t('placementStartDate', 'Start Date')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  className={`cell-end-date ${activeGradebookType}`}
                  onClick={() => handleSort('activityTimezoneEndDate')}
                  sorted={column === 'activityTimezoneEndDate' ? direction : null}>
                  {t('placementEndDate', 'End Date')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  className={`cell-first-name ${activeGradebookType}`}
                  onClick={() => handleSort('firstName')}
                  sorted={column === 'firstName' ? direction : null}>
                  {t('placementFirstName', 'First Name')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  className={`cell-last-name ${activeGradebookType}`}
                  onClick={() => handleSort('lastName')}
                  sorted={column === 'lastName' ? direction : null}>
                  {t('placementLastName', 'Last Name')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  className={`cell-grade ${activeGradebookType}`}
                  onClick={() => handleSort('grade')}
                  sorted={column === 'grade' ? direction : null}
                  textAlign='center'>
                  {t('placementRawScore', 'Raw Score')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  className={`cell-placement ${activeGradebookType}`}
                  textAlign='center'>
                  {t('placementResult', 'Placement')}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {assignmentInstances.filter((instance) => {
                const assignment = assignmentManager.getAssignment(instance.activityId);
                return !gradebookManager.isPresentationMode(assignment);
              }).map((instance, index) => {
                const isBulkChecked = instance.bulkChecked !== undefined ? instance.bulkChecked : false;
                return (
                  <Table.Row
                    key={`placement-row-${instance.id}-${index}`}
                    className={isLoading ? 'loading-row' : ''}
                    disabled={isLoading}
                    tabIndex={0}>
                    {!isReadOnly && (
                      <Table.Cell className={`cell-bulk-select ${activeGradebookType}`}>
                        <SCCheckbox
                          checked={isBulkChecked}
                          onChange={() => handleCheck(instance.id)}
                          tabIndex={0} />
                      </Table.Cell>
                    )}
                    <Table.Cell className={`cell-start-date ${activeGradebookType}`}>
                      <span>{dateFormat(instance.activityTimezoneStartDate, 'twoDigitDate')}</span>
                    </Table.Cell>
                    <Table.Cell className={`cell-end-date ${activeGradebookType}`}>
                      <span>{dateFormat(instance.activityTimezoneEndDate, 'twoDigitDate')}</span>
                    </Table.Cell>
                    <Table.Cell className={`cell-first-name ${activeGradebookType}`}>
                      <span>{instance.firstName}</span>
                    </Table.Cell>
                    <Table.Cell className={`cell-last-name ${activeGradebookType}`}>
                      <span>{instance.lastName}</span>
                    </Table.Cell>
                    <Table.Cell
                      className={`cell-grade ${activeGradebookType}`}
                      textAlign='center'>
                      <span>{renderGradeCell(instance)}</span>
                    </Table.Cell>
                    <Table.Cell
                      className={`cell-placement ${activeGradebookType}`}
                      textAlign='center'>
                      <span>{renderPlacementCell(instance)}</span>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
          <Loader
            active={isLoading && allowLoadMoreAggregateTableRows && hasMoreAggregateTableRows}
            inline='centered'
            style={{ marginBottom: '14px' }} />
        </InfiniteScroll>
      </div>
    </div>
  );
});

SatCoreRegister('GradebookPlacementTable', GradebookPlacementTable);

export default GradebookPlacementTable;
